// The surface-fixture SCATTER — which rocks a world has, where, and of what variant.
//
// This is a PURE function of (addr, R, solidAt). It is deliberately incapable of seeing what has been gathered,
// what has been built, or anything else mutable: it takes no `harvested` argument and reads no live occupancy.
// That is not tidiness, it is the fix for a real bug. The scatter draws from ONE rng stream shared by the gap test,
// the two jitter draws, the variant pick and the cluster rolls, so ANY input that can skip a draw re-rolls every
// rock after it. Deciding visibility inside the loop did exactly that: gathering a single rock moved and re-typed
// the whole remainder of the scatter (measured on 930718.2480.1.3 — 86 rocks pristine, 93 after one gather, with a
// zero-length identical prefix). Subtracting removals is the CALLER's job, afterwards, over a fixed list.
//
// THREE-free, DOM-free AND IMPORT-free on purpose: this file is copied byte-identically to functions/lib/ and
// zone-server/lib/ so the client, the Cloud Function and the zone server derive the same surface from the same bytes.
// That is why makeRng is inlined below rather than imported. Every other twin in this codebase (bit-slot, ore-veins,
// transient-shape, deconstruct, synthesis) has zero relative imports for the same reason: the server copies are .mjs in
// a different directory, so './transient-shape.js' cannot resolve there — and rewriting the specifier in the copy is
// exactly what byte-identity is meant to prevent. transient-shape.js's own header records the same workaround.

// FNV-1a seeded xorshift — byte-identical to makeRng in system-engine.js and transient-shape.js (duplicated so this
// module stands alone). Changing a single operation here re-rolls every surface in the galaxy.
export function makeRng(str) {
    let h = 2166136261 >>> 0;
    for (let i = 0; i < str.length; i++) h = Math.imul(h ^ str.charCodeAt(i), 16777619);
    let a = h >>> 0;
    return () => { a = (a ^ (a << 13)) >>> 0; a = (a ^ (a >>> 17)) >>> 0; a = (a ^ (a << 5)) >>> 0; return (a >>> 0) / 4294967296; };
}

// A rock drops the BIT of its element, not the whole kube. A rock IS a 0.5 cube — the same object a placed bit is —
// so paying out a full kube handed you eight times the volume you picked up, and the thing you got back could not be
// put down as the thing you took. A rock yields exactly itself: 8 rocks make 8 bits, which fill one kube-space and
// collapse into the kube. Geometry, the bitOf economy and what you can rebuild all agree.
export const FIXTURE_VARIANTS = [
    { id: 'stone',   name: 'Stone',        hex: '#8B8781', metal: 0.0,  rough: 0.95, weight: 6, drop: { 'Silica Bit': 1 }, harvestDur: 1.1 },   // common grey silica rock
    { id: 'flint',   name: 'Flint',        hex: '#3B3B40', metal: 0.12, rough: 0.7,  weight: 3, drop: { 'Carbon Bit': 1 }, harvestDur: 1.6 },   // dark carbon-rich rock → the Carbon element (like ferrous → Iron)
    { id: 'ferrous', name: 'Ferrous Rock', hex: '#7A5548', metal: 0.28, rough: 0.75, weight: 2, drop: { 'Iron Bit': 1 },   harvestDur: 1.4 },   // rust-flecked → raw Iron
];
export const VARIANT_BY_ID = Object.fromEntries(FIXTURE_VARIANTS.map(t => [t.id, t]));

// The single item a variant yields — and, once fixtures are seated on the lattice, the NAME the seated bit carries.
// One reader, so "what is this rock" cannot drift between the drop table and the world.
export function bitNameFor(variantId) {
    const v = VARIANT_BY_ID[variantId];
    return v ? Object.keys(v.drop)[0] : null;
}

// DISTRIBUTION: sample POSITIONS per face on a jittered grid, NOT ray directions. A direction-march (normalize a cube
// point → round(dir*r) inward) leaves the outer ~30% of every face empty and stacks many rocks on one cell, so rocks
// read as clumped toward the face centre. A jittered grid gives even-but-random coverage right to the edges; the
// placed-cell set stops stacking; ~40% of grid cells stay empty, and a fraction of placements seed a small cluster.
//
// Returns [{ id:'sx_sy_sz', cell, normal, variant, airCell, slot }] — EVERY natural bit the seed produces, in
// generation order. `cell` is the SURFACE cell it stands on, `airCell` is the cell it occupies and `slot` is which of
// the 27 sub-cell seats it takes there. Nothing here knows about removal.
//
// `top` is the highest cube radius the terrain can reach (the generator's airR). Version 1 never rises above R, so it
// is R and the march starts where it always did; version 2 raises mountains and mesas into the build headroom, and a
// march that started at R + 1 would begin inside them and seat nothing on any of it. It only moves where the march
// STARTS, over cells that are air in every version that existed before it, so no earlier scatter changes.
export function scatterFixtures({ addr, R, solidAt, top }) {
    const out = [];
    if (!R || typeof solidAt !== 'function') return out;
    const TOP = Math.max(R, Number.isInteger(top) ? top : R);
    const rng = makeRng((addr || '') + '#rocks');
    const wsum = FIXTURE_VARIANTS.reduce((s, t) => s + t.weight, 0);
    const pickType = () => { let r = rng() * wsum; for (const t of FIXTURE_VARIANTS) { r -= t.weight; if (r <= 0) return t; } return FIXTURE_VARIANTS[0]; };

    const target = Math.min(220, Math.max(24, Math.round(R * R * 0.24)));
    const perFace = target / 6;
    const n = Math.max(3, Math.ceil(Math.sqrt(perFace * 1.7)));   // grid a touch finer than the rock count → ~0.6 fill leaves random gaps (not a rigid lattice)
    const fill = perFace / (n * n), step = (2 * R) / n;
    const AXT = [[1, 2], [0, 2], [0, 1]];                          // the two TANGENT axes for each normal axis (0=X,1=Y,2=Z)
    const FACES = [[0, 1], [0, -1], [1, 1], [1, -1], [2, 1], [2, -1]];
    const placedCells = new Set();                                 // keyed on the SURFACE cell — one rock per patch of ground
    const reservedAir = new Set();                                 // keyed on the AIR cell — two faces' passes can converge on one
    const _c = [0, 0, 0];

    function placeRockAt(axis, sign, a, bT) {                      // a,bT = tangent grid coords on face (axis,sign)
        if (Math.abs(a) > R || Math.abs(bT) > R) return;
        const t = AXT[axis];
        let sx = 0, sy = 0, sz = 0, found = false;
        for (let nn = TOP + 1; nn >= 0; nn--) {                    // march inward along the face normal to the surface (highest solid in this column — handles hilly terrain)
            _c[0] = _c[1] = _c[2] = 0; _c[axis] = sign * nn; _c[t[0]] = a; _c[t[1]] = bT;
            if (solidAt(_c[0], _c[1], _c[2])) { sx = _c[0]; sy = _c[1]; sz = _c[2]; found = true; break; }
        }
        if (!found) return;
        const ax = Math.abs(sx), ay = Math.abs(sy), az = Math.abs(sz);   // the surface here must genuinely face `axis` — edge columns whose surface flipped to a neighbour belong to THAT face's pass
        const dom = ax >= ay && ax >= az ? 0 : (ay >= az ? 1 : 2);
        if (dom !== axis) return;
        const nx = axis === 0 ? sign : 0, ny = axis === 1 ? sign : 0, nz = axis === 2 ? sign : 0;
        // The fixture stands IN the outward air cell, so that cell must be free of terrain and of any fixture already
        // seated there — ONE OBJECT PER KUBE-SPACE. placedCells cannot cover the second half: it keys on the SURFACE
        // cell, so it never sees two faces' passes converging on one air cell.
        const air = (sx + nx) + '_' + (sy + ny) + '_' + (sz + nz);
        if (solidAt(sx + nx, sy + ny, sz + nz) || reservedAir.has(air)) return;
        const id = sx + '_' + sy + '_' + sz;
        if (placedCells.has(id)) return;
        placedCells.add(id);
        // Every fixture the seed produces draws its variant AND its seat, with no exceptions — that is what makes the
        // scatter a pure function of the seed. Both draws sit after every early return for that reason. See the header.
        const type = pickType();
        // WHICH OF THE 27 SUB-CELL SEATS the bit takes in its air cell. The step on the NORMAL axis is forced so the bit
        // rests ON the ground it was scattered onto — 0 when the air cell is outward of the surface (the bit sits on the
        // cell's low face), 2 when it is inward. The two TANGENT steps are free, which is what keeps a field of natural
        // bits irregular instead of a lattice; all nine combinations touch the supporting face, so none can float.
        //
        // (ai, sign) come from THIS function's own `normal`, deliberately not from bit-slot's cellUpFace/legacySlot: the
        // dominance test above compares abs(sx) while cellUpFace compares abs(sx + 0.5), and the two disagree on a small
        // fraction of seats. Using the record's own normal removes the question rather than relying on them agreeing.
        const st = [1, 1, 1];
        st[axis] = sign > 0 ? 0 : 2;
        st[t[0]] = Math.min(2, Math.floor(rng() * 3));
        st[t[1]] = Math.min(2, Math.floor(rng() * 3));
        const slot = (st[0] * 3 + st[1]) * 3 + st[2];   // stepsToSlot, inlined — this module stays import-free (see header)
        reservedAir.add(air);
        out.push({ id, cell: [sx, sy, sz], normal: [nx, ny, nz], variant: type.id,
                   airCell: [sx + nx, sy + ny, sz + nz], slot });
    }

    for (const [axis, sign] of FACES) {
        for (let i = 0; i < n; i++) for (let j = 0; j < n; j++) {
            if (rng() > fill) continue;                            // random gaps → even but not a rigid lattice
            const a = Math.round(-R + (i + rng()) * step), bT = Math.round(-R + (j + rng()) * step);   // jittered within the grid cell
            const before = placedCells.size;
            placeRockAt(axis, sign, a, bT);
            if (placedCells.size > before && rng() < 0.14) {       // a placement occasionally seeds a small cluster of 1-2 neighbours
                const k = 1 + Math.floor(rng() * 2);
                for (let c = 0; c < k; c++) placeRockAt(axis, sign, a + (Math.round(rng() * 2) - 1), bT + (Math.round(rng() * 2) - 1));
            }
        }
    }
    return out;
}
